import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { queryClient } from '../../../config/queryClient'
import { NotificationKeys } from '../../../enums/notificationKeys'
import { QueryKeys } from '../../../enums/queryKeys'
import { RecurringInvoiceStatusEventType } from '../enums/recurringInvoiceStatusEventType'
import { SetRecurringInvoiceStatusOptions, useSetRecurringInvoiceStatus } from './useSetRecurringInvoiceStatus'

type UseRecurringInvoiceStatusSetterOptions = Omit<SetRecurringInvoiceStatusOptions, 'recurringInvoiceStatus'>

const invalidateQueriesOnStatusChange = (recurringInvoiceId: string | number) => {
  queryClient.invalidateQueries([QueryKeys.RecurringInvoice, String(recurringInvoiceId)])
  queryClient.invalidateQueries([QueryKeys.RecurringInvoiceDraftFormat, String(recurringInvoiceId)])
  queryClient.invalidateQueries([QueryKeys.RecurringInvoices])
  queryClient.invalidateQueries([QueryKeys.RecurringInvoicesSummary])
}

export const useRecurringInvoiceStatus = () => {
  const { t } = useTranslation()

  const { setRecurringInvoiceStatus, isProcessing } = useSetRecurringInvoiceStatus()

  const activateRecurringInvoice = useCallback(
    ({ recurringInvoiceId, onSuccess, onError }: UseRecurringInvoiceStatusSetterOptions) => {
      setRecurringInvoiceStatus({
        recurringInvoiceId,
        recurringInvoiceStatus: RecurringInvoiceStatusEventType.Started,
        onSuccess: (recurringInvoiceData) => {
          if (recurringInvoiceData?.id) {
            invalidateQueriesOnStatusChange(recurringInvoiceData.id)
          }
          onSuccess?.(recurringInvoiceData)
        },
        onError: () => {
          onError?.()
        },
      })
    },
    [setRecurringInvoiceStatus],
  )

  const resumeRecurringInvoice = useCallback(
    ({ recurringInvoiceId, onSuccess, onError }: UseRecurringInvoiceStatusSetterOptions) => {
      setRecurringInvoiceStatus({
        recurringInvoiceId,
        recurringInvoiceStatus: RecurringInvoiceStatusEventType.Started,
        onSuccess: (recurringInvoiceData) => {
          if (recurringInvoiceData?.id) {
            invalidateQueriesOnStatusChange(recurringInvoiceData.id)
          }
          notify({
            id: NotificationKeys.RecurringInvoiceStatusUpdate,
            message: t('recurring_invoices.resume.notification.success'),
            variant: 'success',
          })
          onSuccess?.(recurringInvoiceData)
        },
        onError: () => {
          notify({
            id: NotificationKeys.RecurringInvoiceStatusUpdate,
            message: t('recurring_invoices.resume.notification.error'),
            variant: 'error',
          })
          onError?.()
        },
      })
    },
    [setRecurringInvoiceStatus, t],
  )

  const pauseRecurringInvoice = useCallback(
    ({ recurringInvoiceId, onSuccess, onError }: UseRecurringInvoiceStatusSetterOptions) => {
      setRecurringInvoiceStatus({
        recurringInvoiceId,
        recurringInvoiceStatus: RecurringInvoiceStatusEventType.Paused,
        onSuccess: (recurringInvoiceData) => {
          if (recurringInvoiceData?.id) {
            invalidateQueriesOnStatusChange(recurringInvoiceData.id)
          }
          notify({
            id: NotificationKeys.RecurringInvoiceStatusUpdate,
            message: t('recurring_invoices.pause.notification.success'),
            variant: 'success',
          })
          onSuccess?.(recurringInvoiceData)
        },
        onError: () => {
          notify({
            id: NotificationKeys.RecurringInvoiceStatusUpdate,
            message: t('recurring_invoices.pause.notification.error'),
            variant: 'error',
          })
          onError?.()
        },
      })
    },
    [setRecurringInvoiceStatus, t],
  )

  const completeRecurringInvoice = useCallback(
    ({ recurringInvoiceId, onSuccess, onError }: UseRecurringInvoiceStatusSetterOptions) => {
      setRecurringInvoiceStatus({
        recurringInvoiceId,
        recurringInvoiceStatus: RecurringInvoiceStatusEventType.Completed,
        onSuccess: (recurringInvoiceData) => {
          if (recurringInvoiceData?.id) {
            invalidateQueriesOnStatusChange(recurringInvoiceData.id)
          }
          notify({
            id: NotificationKeys.RecurringInvoiceStatusUpdate,
            message: t('recurring_invoices.mark_as_completed.notification.success'),
            variant: 'success',
          })
          onSuccess?.(recurringInvoiceData)
        },
        onError: () => {
          notify({
            id: NotificationKeys.RecurringInvoiceStatusUpdate,
            message: t('recurring_invoices.mark_as_completed.notification.error'),
            variant: 'error',
          })
          onError?.()
        },
      })
    },
    [setRecurringInvoiceStatus, t],
  )

  return {
    activateRecurringInvoice,
    completeRecurringInvoice,
    isProcessing,
    pauseRecurringInvoice,
    resumeRecurringInvoice,
  }
}
